import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { format } from 'date-fns';
import { useAsyncAutocomplete } from '@/__core/hocs/useAsyncAutocomplete';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Autocomplete,
  TextField,
  Chip,
  CircularProgress,
  FormControl,
} from '@mui/material';
import AppForm, { AppFormSection } from '@/__core/components/AppForm';
import { UserOptionDisplay } from '@/__core/components';
import { LabeledField } from '@/__core/components/LabeledField';
import { sharedFormStyles } from '@/__core/styles/sharedFormStyles';
import MessageSnackbar from '@/__core/helpers/components/MessageSnackbar';
import { formatErrorForSnackbar } from '@/__core/helpers/Error';
import { getCurrentCompanyId } from '@/modules/auth/helpers/CompanyStorage';
import { getEmployees } from '@/modules/employees/redux/EmployeeCRUD';
import {
  updateOrderProgressItem,
  createOrderProgressItem,
  getOrdersForProgress,
} from '../redux/OrderProgressCRUD';
import { type OrderProgressOption } from '@/utils/orderFormatLine';
import type { OrderProgressCreatedItem } from '../models/OrderProgressModel';

interface ProgressItem {
  id?: string;
  date: string;
  employee_ids: string[];
  employee_names?: string[];
  note: string;
  order_id?: string;
  order_code?: string;
  customer_name?: string;
  sales_employee_name?: string;
  delivery_address_line?: string;
  created_at?: string;
  updated_at?: string;
  created_by_name?: string;
  updated_by_name?: string;
}

interface OrderOption extends OrderProgressOption {}

interface EmployeeOption {
  id: string;
  name: string;
  nickname?: string | null;
  avatar?: string | null;
  image1?: string | null;
}

const formatOrderProgressLabel = (
  code?: string | null,
  customerName?: string | null,
): string => {
  const orderCode = code?.trim() || 'N/A';
  const customer = customerName?.trim();
  return customer ? `${orderCode}: ${customer}` : orderCode;
};

const parseLocalDate = (value: string | null | undefined): Date | null => {
  if (!value) return null;
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
};

const formatLocalDate = (value: Date | null): string => {
  if (!value) return '';
  return format(value, 'yyyy-MM-dd');
};

const multiEmployeeAutocompleteSx = {
  ...sharedFormStyles.autocompleteFullWidth,
  '& .MuiAutocomplete-inputRoot': {
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    height: 'auto !important',
    minHeight: 40,
    py: '4px',
    pr: '28px !important',
  },
  '& .MuiAutocomplete-input': {
    minWidth: '48px !important',
  },
  '& .MuiAutocomplete-tag': {
    margin: '2px',
    maxWidth: '100%',
  },
};

interface EditProgressModalProps {
  open: boolean;
  onClose: () => void;
  item: ProgressItem | null;
  onUpdate?: () => void;
  onItemCreated?: (createdItem: OrderProgressCreatedItem) => void;
  onSuccess?: (message: string) => void;
  disabled?: boolean;
  mode?: 'create' | 'edit';
  /** Render as full-page form inside module tab (no Dialog). */
  embedded?: boolean;
  isActive?: boolean;
  tabId?: number;
  onUnsavedChanges?: (hasChanges: boolean, tabId?: number) => void;
  /** Resolved company id from toolbar (resolveActiveCompanyFilter). */
  activeCompanyFilter?: string;
}

export default function EditProgressModal({
  open,
  onClose,
  item,
  onUpdate,
  onItemCreated,
  onSuccess,
  disabled = false,
  mode = 'edit',
  embedded = false,
  isActive = true,
  tabId,
  onUnsavedChanges,
  activeCompanyFilter,
}: EditProgressModalProps) {
  const isCreateMode = mode === 'create';
  const isFormVisible = embedded ? isActive : open;
  const companyIdForPayload =
    activeCompanyFilter && activeCompanyFilter !== 'all' && activeCompanyFilter !== 'global'
      ? activeCompanyFilter
      : getCurrentCompanyId() || '';
  const [employees, setEmployees] = useState<EmployeeOption[]>([]);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [employeesError, setEmployeesError] = useState<string | null>(null);

  const [formData, setFormData] = useState<{
    order_id: string | null;
    date: Date | null;
    employee_ids: string[];
    note: string;
  }>({
    order_id: null,
    date: null,
    employee_ids: [],
    note: '',
  });

  const [selectedOrder, setSelectedOrder] = useState<OrderOption | null>(null);
  const [error, setError] = useState<string | undefined>(undefined);
  const [isSaving, setIsSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const originalFormData = React.useRef<{
    order_id: string | null;
    date: Date | null;
    employee_ids: string[];
    note: string;
  }>({ order_id: null, date: null, employee_ids: [], note: '' });

  useEffect(() => {
    const loadMissionEmployees = async () => {
      if (!isFormVisible) {
        return;
      }

      if (!companyIdForPayload) {
        setEmployees([]);
        setEmployeesError('Không tìm thấy công ty đang chọn');
        return;
      }

      try {
        setEmployeesLoading(true);
        setEmployeesError(null);

        const response = await getEmployees({
          company: companyIdForPayload,
          mission: 'GIAO_HANG',
          no_pagination: true,
        });
        const employeeList =
          response.data?.results || (Array.isArray(response.data) ? response.data : []);
        const unique = new Map<string, EmployeeOption>();

        employeeList.forEach((employee: any) => {
          const employeeId = employee?.id || '';
          const employeeName = employee?.name || '';
          const employeeNickname = employee?.nickname || '';
          const employeeAvatar = employee?.user?.avatar || null;
          const employeeImage1 = employee?.image1 || null;
          if (employeeId && employeeName && !unique.has(employeeId)) {
            unique.set(employeeId, {
              id: employeeId,
              name: employeeName,
              nickname: employeeNickname,
              avatar: employeeAvatar,
              image1: employeeImage1,
            });
          }
        });

        setEmployees(Array.from(unique.values()));
      } catch {
        setEmployees([]);
        setEmployeesError('Không thể tải danh sách nhân viên giao hàng');
      } finally {
        setEmployeesLoading(false);
      }
    };

    void loadMissionEmployees();
  }, [companyIdForPayload, isFormVisible]);

  useEffect(() => {
    if (isFormVisible) {
      setSuccessMessage(null);
      setError(undefined);
    }
  }, [isFormVisible]);

  const formatOrderLabel = useCallback((order: OrderOption | string | null) => {
    if (!order) return '';
    if (typeof order === 'string') return order;
    return order.label || formatOrderProgressLabel(order.code, order.customer_name);
  }, []);

  const fetchOrderOptions = useCallback(
    (search: string) => getOrdersForProgress(search, companyIdForPayload || undefined),
    [companyIdForPayload],
  );

  const orderAutocomplete = useAsyncAutocomplete<OrderOption>({
    fetchOptions: fetchOrderOptions,
    enabled: isCreateMode && isFormVisible,
    value: selectedOrder,
    getOptionLabel: formatOrderLabel,
    getOptionId: (option) => option.id,
  });

  const buildOrderOptionFromItem = useCallback(
    (source: ProgressItem): OrderOption => ({
      id: source.order_id!,
      code: source.order_code || '',
      customer_name: source.customer_name || '',
      label: formatOrderProgressLabel(source.order_code, source.customer_name),
      status_name: '',
      status_color: '#64748b',
      line1Color: '#64748b',
      line2Color: '#6b7280',
      addressLabel: '',
    }),
    [],
  );

  useEffect(() => {
    if (!isFormVisible) {
      return;
    }

    if (isCreateMode) {
      if (item) {
        const initialDate = item.date ? new Date(item.date) : new Date();
        const initialEmployeeIds = item.employee_ids || [];
        const initialNote = item.note || '';
        const initialOrderId = item.order_id || null;

        setFormData({
          order_id: initialOrderId,
          date: initialDate,
          employee_ids: initialEmployeeIds,
          note: initialNote,
        });
        originalFormData.current = {
          order_id: initialOrderId,
          date: initialDate,
          employee_ids: [...initialEmployeeIds],
          note: initialNote,
        };
        setSelectedOrder(
          initialOrderId && item.order_code ? buildOrderOptionFromItem(item) : null,
        );
      } else {
        const today = new Date();
        setFormData({
          order_id: null,
          date: today,
          employee_ids: [],
          note: '',
        });
        originalFormData.current = {
          order_id: null,
          date: today,
          employee_ids: [],
          note: '',
        };
        setSelectedOrder(null);
      }
      return;
    }

    if (item) {
      const initialDate = item.date ? new Date(item.date) : null;
      const initialEmployeeIds = item.employee_ids || [];
      const initialNote = item.note || '';
      const initialOrderId = item.order_id || null;

      setFormData({
        order_id: initialOrderId,
        date: initialDate,
        employee_ids: initialEmployeeIds,
        note: initialNote,
      });
      originalFormData.current = {
        order_id: initialOrderId,
        date: initialDate,
        employee_ids: [...initialEmployeeIds],
        note: initialNote,
      };
    }
    setError(undefined);
  }, [item, isFormVisible, isCreateMode, buildOrderOptionFromItem]);

  const hasChanges = React.useMemo(() => {
    if (isCreateMode) {
      const original = originalFormData.current;

      if (item) {
        const orderChanged = formData.order_id !== original.order_id;
        const dateChanged =
          formData.date?.toISOString().split('T')[0] !== original.date?.toISOString().split('T')[0];
        const employeesChanged =
          formData.employee_ids.length !== original.employee_ids.length ||
          !formData.employee_ids.every((id) => original.employee_ids.includes(id));
        const noteChanged = formData.note !== original.note;

        return orderChanged || dateChanged || employeesChanged || noteChanged;
      }

      return !!(formData.order_id && formData.date && formData.employee_ids.length > 0);
    }

    if (!item) return false;
    const original = originalFormData.current;

    const dateChanged =
      formData.date?.toISOString().split('T')[0] !== original.date?.toISOString().split('T')[0];
    const employeesChanged =
      formData.employee_ids.length !== original.employee_ids.length ||
      !formData.employee_ids.every((id) => original.employee_ids.includes(id));
    const noteChanged = formData.note !== original.note;

    return dateChanged || employeesChanged || noteChanged;
  }, [formData, item, isCreateMode]);

  useEffect(() => {
    if (!embedded || !onUnsavedChanges) {
      return;
    }
    onUnsavedChanges(hasChanges, tabId);
  }, [embedded, hasChanges, onUnsavedChanges, tabId]);

  const selectedEmployees = useMemo(() => {
    if (!formData.employee_ids || formData.employee_ids.length === 0) {
      return [];
    }

    const byId = new Map<string, EmployeeOption>();
    employees.forEach((employee) => {
      byId.set(employee.id, employee);
    });

    formData.employee_ids.forEach((employeeId, index) => {
      if (!byId.has(employeeId)) {
        const fallbackName = item?.employee_names?.[index] || `Nhân viên ${employeeId}`;
        byId.set(employeeId, { id: employeeId, name: fallbackName, nickname: undefined, avatar: undefined, image1: undefined });
      }
    });

    return formData.employee_ids
      .map((employeeId) => byId.get(employeeId))
      .filter((employee): employee is EmployeeOption => Boolean(employee));
  }, [formData.employee_ids, employees, item?.employee_names]);

  const handleInputChange = (field: keyof typeof formData, value: unknown) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    if (error) {
      setError(undefined);
    }
  };

  const handleFormChange = (key: string, value: unknown) => {
    if (key === 'date') {
      handleInputChange('date', parseLocalDate(String(value || '')));
      return;
    }
    if (key === 'note') {
      handleInputChange('note', String(value || ''));
    }
  };

  const handleSubmit = async () => {
    if (isCreateMode && !formData.order_id) {
      setError('Mã đơn hàng không được để trống');
      return;
    }

    if (!isCreateMode && (!item || !item.id)) {
      setError('Không tìm thấy ID tiến độ để cập nhật');
      return;
    }

    if (!formData.date) {
      setError('Ngày không được để trống');
      return;
    }

    if (!formData.employee_ids || formData.employee_ids.length === 0) {
      setError('Nhân viên không được để trống');
      return;
    }

    if (!companyIdForPayload) {
      setError('Không xác định được công ty từ thanh công cụ');
      return;
    }

    setError(undefined);
    setIsSaving(true);

    try {
      const dateStr = formData.date.toISOString().split('T')[0];

      if (isCreateMode) {
        const createdItem = await createOrderProgressItem({
          order_id: formData.order_id!,
          date: dateStr,
          employee_ids: formData.employee_ids,
          note: formData.note,
          company: companyIdForPayload,
        });

        onUpdate?.();

        if (!createdItem.id) {
          throw new Error('Created item does not have an id');
        }

        const newProgressItem: OrderProgressCreatedItem = {
          id: createdItem.id,
          date: createdItem.date || dateStr,
          employee_ids: createdItem.employee_ids || formData.employee_ids,
          employee_names: createdItem.employee_names || [],
          note: createdItem.note || formData.note,
          order_id: createdItem.order_id || formData.order_id,
          order_code: createdItem.order_code || '',
          customer_name: createdItem.customer_name || '',
          sales_employee_name: createdItem.sales_employee_name || '',
          delivery_address_line: createdItem.delivery_address_line || '',
          created_at: createdItem.created_at || new Date().toISOString(),
          updated_at: createdItem.updated_at,
          created_by_name: createdItem.created_by_name,
          updated_by_name: createdItem.updated_by_name,
        };

        onItemCreated?.(newProgressItem);

        const createdDate = createdItem.date ? new Date(createdItem.date) : formData.date;
        setFormData({
          order_id: newProgressItem.order_id || formData.order_id,
          date: createdDate,
          employee_ids: newProgressItem.employee_ids || formData.employee_ids,
          note: newProgressItem.note || formData.note,
        });

        originalFormData.current = {
          order_id: newProgressItem.order_id || formData.order_id,
          date: createdDate,
          employee_ids: [...(newProgressItem.employee_ids || formData.employee_ids)],
          note: newProgressItem.note || formData.note,
        };

        onSuccess?.('Tạo mới thành công!');
        setError(undefined);
        if (!embedded) {
          handleClose();
        }
      } else {
        await updateOrderProgressItem(item!.id!, {
          date: dateStr,
          employee_ids: formData.employee_ids,
          note: formData.note,
          company: companyIdForPayload,
        });

        onUpdate?.();

        originalFormData.current = {
          order_id: formData.order_id,
          date: formData.date,
          employee_ids: [...formData.employee_ids],
          note: formData.note,
        };

        onSuccess?.('Cập nhật thành công!');
        setError(undefined);
        if (!embedded) {
          handleClose();
        }
      }
    } catch (submitError: unknown) {
      const errorMsg = isCreateMode ? 'Có lỗi xảy ra khi tạo mới' : 'Có lỗi xảy ra khi cập nhật';
      setError(formatErrorForSnackbar(submitError as Error) || errorMsg);
      setSuccessMessage(null);
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    setFormData({
      order_id: null,
      date: isCreateMode ? new Date() : null,
      employee_ids: [],
      note: '',
    });
    setSelectedOrder(null);
    orderAutocomplete.reset();
    setError(undefined);
    setSuccessMessage(null);
    onClose();
  };

  const orderDisplayText =
    !isCreateMode && item ? formatOrderProgressLabel(item.order_code, item.customer_name) : '';

  const fieldsDisabled = disabled || isSaving;
  const orderFieldError = error?.includes('Mã đơn hàng') ? error : undefined;
  const dateFieldError = error?.includes('Ngày') ? error : undefined;
  const employeeFieldError = error?.includes('Nhân viên') ? error : undefined;

  const renderOrderOptionLabel = (option: OrderOption) => (
    <Typography
      variant="body2"
      sx={{
        fontSize: '0.875rem',
        color: option.line1Color || option.status_color || '#0f172a',
      }}
    >
      {option.label}
    </Typography>
  );

  const sections = useMemo<AppFormSection[]>(() => {
    const fields: AppFormSection['fields'] = [];

    if (isCreateMode) {
      fields.push({
        key: 'order_id',
        label: '',
        type: 'custom',
        standalone: true,
        render: () => (
          <LabeledField
            label="Mã đơn hàng"
            required
            labelWidth={120}
            alignItems="start"
            fieldAlign="start"
            error={!!orderFieldError}
            helperText={orderFieldError}
            sx={{ width: '100%', mb: 1.5 }}
          >
            <FormControl fullWidth size="small">
              <Autocomplete
                {...orderAutocomplete.autocompleteProps}
                fullWidth
                size="small"
                sx={{ minWidth: 0, ...sharedFormStyles.inputArrowAlign }}
                value={selectedOrder}
            onChange={(_event, newValue) => {
              setSelectedOrder(newValue);
              handleInputChange('order_id', newValue?.id || null);
            }}
            getOptionLabel={(option) => formatOrderLabel(option)}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            disabled={fieldsDisabled}
            renderInput={(params) => (
              <TextField
                {...params}
                fullWidth
                placeholder="Chọn hoặc tìm kiếm đơn hàng"
                error={!!orderFieldError}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {orderAutocomplete.loading ? <CircularProgress size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            renderOption={(props, option) => (
              <Box component="li" {...props} key={option.id}>
                {renderOrderOptionLabel(option)}
              </Box>
            )}
              />
            </FormControl>
          </LabeledField>
        ),
      });
    } else {
      fields.push({
        key: 'order_display',
        label: 'Mã đơn hàng',
        type: 'text',
        labelWidth: 120,
        disabled: true,
      });
    }

    fields.push(
      {
        key: 'date',
        label: 'Ngày',
        type: 'date',
        labelWidth: 120,
        required: true,
        disabled: fieldsDisabled,
        error: !!dateFieldError,
        helperText: dateFieldError,
        displayFormat: 'dd/MM/yyyy',
        parseValue: (value) => parseLocalDate(String(value || '')),
        formatValue: (date) => formatLocalDate(date),
      },
      {
        key: 'employee_ids',
        label: '',
        type: 'custom',
        standalone: true,
        render: () => (
          <LabeledField
            label="Nhân viên"
            required
            labelWidth={120}
            alignItems="start"
            fieldAlign="start"
            error={!!employeeFieldError}
            helperText={employeeFieldError}
            sx={{ width: '100%', mb: 1.5 }}
          >
            <FormControl fullWidth size="small" sx={{ display: 'block', width: '100%' }}>
              <Autocomplete
                multiple
                fullWidth
                size="small"
                sx={multiEmployeeAutocompleteSx}
                options={employees}
                value={selectedEmployees}
                onChange={(_event, newValue) => {
                  handleInputChange(
                    'employee_ids',
                    newValue.map((emp) => emp.id),
                  );
                }}
                getOptionLabel={(option) => option.nickname || option.name}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                disabled={fieldsDisabled || employeesLoading}
                loading={employeesLoading}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option.id}
                      label={option.nickname || option.name}
                      size="small"
                      sx={sharedFormStyles.multiSelectChip}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    size="small"
                    placeholder="Chọn nhân viên giao hàng"
                    error={!!employeeFieldError}
                    sx={sharedFormStyles.multiSelectInput}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {employeesLoading ? <CircularProgress size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <UserOptionDisplay
                      user={{
                        id: option.id,
                        name: option.name,
                        nickname: option.nickname,
                        avatar: option.avatar,
                        image1: option.image1 || null,
                      }}
                    />
                  </Box>
                )}
              />
              {employeesError ? (
                <Typography sx={{ color: 'error.main', fontSize: '0.75rem', mt: 0.5 }}>
                  {employeesError}
                </Typography>
              ) : null}
            </FormControl>
          </LabeledField>
        ),
      },
      {
        key: 'note',
        label: '',
        type: 'custom',
        standalone: true,
        render: () => (
          <LabeledField
            label="Ghi chú"
            labelWidth={120}
            alignItems="start"
            fieldAlign="start"
            sx={{ width: '100%' }}
          >
            <textarea
              value={formData.note}
              onChange={(e) => handleFormChange('note', e.target.value)}
              disabled={fieldsDisabled}
              placeholder="Nhập ghi chú..."
              rows={3}
              style={sharedFormStyles.noteTextarea(!fieldsDisabled)}
            />
          </LabeledField>
        ),
      },
    );

    return [
      {
        key: 'order-progress-main',
        disablePaper: !embedded,
        fields,
      },
    ];
  }, [
    embedded,
    employeeFieldError,
    employees,
    employeesError,
    employeesLoading,
    fieldsDisabled,
    formData.note,
    formatOrderLabel,
    handleFormChange,
    handleInputChange,
    isCreateMode,
    orderAutocomplete,
    orderFieldError,
    dateFieldError,
    selectedEmployees,
    selectedOrder,
  ]);

  const formValues = useMemo(
    () => ({
      order_display: orderDisplayText,
      date: formatLocalDate(formData.date),
      note: formData.note,
    }),
    [formData.date, formData.note, orderDisplayText],
  );

  const submitDisabled =
    fieldsDisabled ||
    !hasChanges ||
    !formData.date ||
    formData.employee_ids.length === 0 ||
    (isCreateMode && !formData.order_id);

  const appForm = (
    <AppForm
      sections={sections}
      values={formValues}
      onChange={handleFormChange}
      onSubmit={handleSubmit}
      onCancel={handleClose}
      submitting={isSaving}
      submitDisabled={submitDisabled}
      submitLabel={isCreateMode ? 'Tạo mới' : 'Cập nhật'}
      submittingLabel={isCreateMode ? 'Đang tạo...' : 'Đang cập nhật...'}
      cancelLabel="Hủy"
      cancelDisabled={fieldsDisabled}
      containerSx={{ p: 0, m: 0, width: '100%', backgroundColor: 'transparent' }}
      actionBarSx={embedded ? undefined : { px: 0, pb: 0, pt: 2 }}
    />
  );

  if (!isFormVisible) {
    return null;
  }

  return (
    <>
      {embedded ? (
        <Box sx={{ height: '100%', overflow: 'auto', p: 3, width: '100%' }}>{appForm}</Box>
      ) : (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
          <DialogContent dividers sx={{ pt: 2 }}>{appForm}</DialogContent>
        </Dialog>
      )}

      {error && !orderFieldError && !dateFieldError && !employeeFieldError ? (
        <MessageSnackbar
          open
          message={error}
          severity="error"
          onClose={() => setError(undefined)}
        />
      ) : null}

      {successMessage ? (
        <MessageSnackbar
          open
          message={successMessage}
          severity="success"
          onClose={() => setSuccessMessage(null)}
        />
      ) : null}
    </>
  );
}
